/**
 * The board while they are still out there.
 *
 * A different component from Leaderboard.tsx on purpose, because it
 * answers a different question and is ordered on a different number.
 * Migration 0120 has the argument; the short of it is that a board of
 * half-finished cards ranked on the TOTAL puts whoever has played fewest
 * holes on top, and looks broken to everybody reading it.
 *
 * So the column people read is to par for what has been played, "thru"
 * is beside every name, and the whole thing says out loud that it is not
 * a result. When the cards are in, Leaderboard is what says who won.
 *
 * It refreshes itself, because it is read on a phone in a buggy and on a
 * screen behind the bar, and neither of those has anybody to press a
 * button.
 */

import { useEffect, useState } from 'react';
import { useApi } from '../lib/useApi.ts';
import { Card, Empty, ErrorBanner, Loading, Status } from './ui.tsx';
import { FORMAT_LABEL } from './Leaderboard.tsx';

export interface LiveRow {
  score_id: string;
  person_id: string;
  player_name: string;
  thru: number;
  current_hole: number | null;
  gross: number;
  to_par: number;
  net_to_par: number;
  points: number;
  standing: number;
  place: number;
  is_complete: boolean;
  last_hole_at: string | null;
}

interface LivePayload {
  competition: {
    competition_id: string;
    name: string;
    event_name: string;
    format: string;
    status: string;
  };
  board: LiveRow[];
  still_playing: number;
}

/** "+2", "E", "-1". */
export function toPar(n: number): string {
  if (n === 0) return 'E';
  return n > 0 ? `+${n}` : String(n);
}

export function LiveBoard({
  competitionId, refreshSeconds = 60, showTitle = true,
}: { competitionId: string; refreshSeconds?: number; showTitle?: boolean }) {
  const [tick, setTick] = useState(0);
  const q = useApi<LivePayload>(
    `/v1/golf/competitions/${competitionId}/live`, [competitionId, tick]);

  // Nobody presses a button on a screen behind the bar.
  useEffect(() => {
    if (refreshSeconds <= 0) return undefined;
    const t = setInterval(() => setTick((n) => n + 1), refreshSeconds * 1000);
    return () => clearInterval(t);
  }, [refreshSeconds]);

  const d = q.data;
  if (q.loading && !d) return <Loading />;
  if (!d) return <ErrorBanner error={q.error} />;

  const stableford = d.competition.format === 'stableford';
  const shared = new Map<number, number>();
  for (const r of d.board) shared.set(r.place, (shared.get(r.place) ?? 0) + 1);

  const body = (
    <>
      <div className="row leader__state">
        {/* Said plainly. A live board that looks like a result is worse
            than no board, and this one is never a result. */}
        <Status tone="warning">
          {d.still_playing > 0
            ? `Live — ${d.still_playing} still out`
            : 'Everybody in, not yet declared'}
        </Status>
        <span className="muted small">
          {FORMAT_LABEL[d.competition.format] ?? d.competition.format}
          {' · ordered on how each player is going, not on the total'}
        </span>
      </div>

      {d.board.length === 0 ? (
        <Empty>
          Nobody has a score on a card yet. This fills in as people play — a member
          keeping their own card is what puts them on it.
        </Empty>
      ) : (
        <table className="data leader">
          <thead>
            <tr>
              <th className="num">Pos</th>
              <th>Player</th>
              <th className="num">{stableford ? 'Points' : 'Score'}</th>
              <th className="num">Thru</th>
            </tr>
          </thead>
          <tbody>
            {d.board.map((r) => (
              <tr key={r.score_id}>
                <td className="num mono">
                  {(shared.get(r.place) ?? 1) > 1 ? 'T' : ''}{r.place}
                </td>
                <td>
                  {r.player_name}
                  {r.is_complete && <div className="hint">in</div>}
                </td>
                <td className="num">
                  <strong>{stableford ? r.points : toPar(r.net_to_par)}</strong>
                  {!stableford && (
                    <div className="hint">{r.gross} shots</div>
                  )}
                </td>
                <td className="num">
                  {r.is_complete
                    ? <span className="muted">F</span>
                    : (
                      <>
                        {r.thru}
                        {r.current_hole && (
                          <div className="hint">on {r.current_hole}</div>
                        )}
                      </>
                    )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );

  if (!showTitle) return body;

  return (
    <Card title={`${d.competition.event_name} — ${d.competition.name}`}>
      <ErrorBanner error={q.error} />
      {body}
    </Card>
  );
}
